import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AssignmentReturn,
  AttachMoney,
  Business,
  Close,
  EditLocationAlt,
  Inventory,
  LocalShipping,
  Refresh,
  ShoppingCart,
  Star,
  Storefront,
  TrendingUp,
  CheckCircle,
  PendingActions,
} from "@mui/icons-material";
import ReactApexChart from "react-apexcharts";
import { getData, putData } from "../../Webservice/webservice";
import MapPicker from "../../Components/MapPicker";

const FONT = "Mulish, sans-serif";
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EMPTY_STATS = {
  fulfillmentRate: 0, returnRate: 0, averageDispatchTimeHours: 0, deliverySuccessRate: 0,
  requests: { total: 0, pending: 0, inProgress: 0, delivered: 0, rejected: 0 },
  products: { listed: 0 }, payout: { pending: 0 },
};

// status chips
const REQUEST_TONE = { pending: ["#fef3c7", "#92400e"], accepted: ["#e0e7ff", "#3730a3"], packed: ["#e0f2fe", "#075985"], shipped: ["#dbeafe", "#1d4ed8"], delivered: ["#dcfce7", "#166534"], rejected: ["#fee2e2", "#991b1b"] };
const RETURN_TONE = { initiated: ["#fef3c7", "#92400e"], completed: ["#dcfce7", "#166534"], cancelled: ["#f1f5f9", "#475569"] };
const StatusChip = ({ status, tones }) => {
  const [bg, fg] = tones[String(status || "").toLowerCase()] || ["#f1f5f9", "#475569"];
  return <Chip size="small" label={status || "-"} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "0.65rem", height: 22, textTransform: "uppercase", background: bg, color: fg }} />;
};

const Card = ({ title, subtitle, icon, accent = "#6366f1", right, children, sx, tour }) => (
  <Paper elevation={0} data-tour={tour} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "16px", border: "1px solid #e2e8f0", borderTop: `4px solid ${accent}`, minWidth: 0, ...sx }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, color: "#0f172a", display: "flex", alignItems: "center", gap: 0.75 }}>{icon}{title}</Typography>
        {subtitle && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#64748b" }}>{subtitle}</Typography>}
      </Box>
      {right}
    </Box>
    {children}
  </Paper>
);

const Kpi = ({ label, value, sub, icon, from, to, onClick, loading }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex", alignItems: "center", gap: 1.5, p: 2, borderRadius: "16px", color: "#fff", minWidth: 0,
      background: `linear-gradient(135deg, ${from}, ${to})`, cursor: onClick ? "pointer" : "default",
      transition: "transform .2s", "&:hover": onClick ? { transform: "translateY(-3px)" } : undefined,
    }}
  >
    <Box sx={{ width: 46, height: 46, borderRadius: "14px", background: "rgba(255,255,255,.22)", display: "grid", placeItems: "center", fontSize: 24, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, opacity: 0.92 }}>{label}</Typography>
      {loading ? <Skeleton width={70} height={34} sx={{ bgcolor: "rgba(255,255,255,.3)" }} /> : <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>}
      {sub && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.88 }}>{sub}</Typography>}
    </Box>
  </Box>
);

const Rate = ({ label, value, suffix = "%", good = "high", hint }) => {
  const n = Number(value) || 0;
  const pct = suffix === "%" ? Math.min(100, n) : Math.min(100, n);
  const ok = good === "high" ? n >= 80 : n <= 10;
  const mid = good === "high" ? n >= 50 : n <= 25;
  const color = ok ? "#16a34a" : mid ? "#f59e0b" : "#ef4444";
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: "#475569" }}>{label}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{value}{suffix}</Typography>
      </Box>
      {suffix === "%" && <LinearProgress variant="determinate" value={pct} sx={{ height: 7, borderRadius: 4, background: "#eef2f7", "& .MuiLinearProgress-bar": { background: color, borderRadius: 4 } }} />}
      {hint && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: "#94a3b8", mt: 0.5 }}>{hint}</Typography>}
    </Box>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [graph, setGraph] = useState({ invoiceStats: [], returnStats: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentReturns, setRecentReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(0);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [openLocationModal, setOpenLocationModal] = useState(false);
  const [formData, setFormData] = useState({
    businessAddress: "", businessCity: "", businessState: "", businessPincode: "",
    storeAddress: "", storeCity: "", storeState: "", storePincode: "",
  });
  const [loggedInName, setLoggedInName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const tokenValue = sessionStorage.getItem("token");
    const token = tokenValue ? `Bearer ${tokenValue}` : "";
    let sellerId = null;
    try {
      const sellerUser = JSON.parse(localStorage.getItem("sellerUser") || sessionStorage.getItem("user") || "null");
      if (sellerUser) { setLoggedInName(sellerUser.name || sellerUser.firstName || "Seller"); sellerId = sellerUser.id || sellerUser._id; }
    } catch (e) { /* the greeting falls back to "Seller" */ }
    if (!sellerId) {
      const raw = localStorage.getItem("sellerUserId") || sessionStorage.getItem("userId");
      sellerId = raw ? raw.replace(/['"]+/g, "") : null;
    }

    // all the calls run together; one failing does not blank the page
    const calls = await Promise.allSettled([
      getData("seller/dashboard/summary", null, token),
      getData("seller/dashboard/monthly-graph", null, token),
      getData("seller/dashboard/top-products", null, token),
      getData("seller/dashboard/recent-request", null, token),
      getData("seller/dashboard/recent-return", null, token),
      sellerId ? getData(`seller/${sellerId}`, null, token) : Promise.resolve(null),
    ]);
    const [summary, monthly, top, requests, returns, profile] = calls.map((c) => (c.status === "fulfilled" ? c.value : null));
    setFailed(calls.slice(0, 5).filter((c) => c.status === "rejected").length);

    if (summary) setStats({ ...EMPTY_STATS, ...summary, requests: { ...EMPTY_STATS.requests, ...summary.requests }, products: { ...EMPTY_STATS.products, ...summary.products }, payout: { ...EMPTY_STATS.payout, ...summary.payout } });
    if (monthly) setGraph({ invoiceStats: monthly.invoiceStats || [], returnStats: monthly.returnStats || [] });
    setTopProducts(top?.topSuppliedProducts || []);
    setRecentRequests(requests?.recentRequests || []);
    setRecentReturns(returns?.recentReturns || []);
    if (profile) {
      setSellerInfo(profile);
      setFormData({
        businessAddress: profile.businessAddress || "", businessCity: profile.businessCity || "", businessState: profile.businessState || "", businessPincode: profile.businessPincode || "",
        storeAddress: profile.storeAddress || "", storeCity: profile.storeCity || "", storeState: profile.storeState || "", storePincode: profile.storePincode || "",
        storeLatitude: profile.storeLatitude || "", storeLongitude: profile.storeLongitude || "",
      });
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const fetchAddressFromLatLng = async (lat, lng) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
      );
      const data = await response.json();
      
      if (data.status !== 'OK') {
        console.error('Google Geocoding error:', data.status);
        return null;
      }

      const result = data.results[0];
      const addressComponents = result.address_components;
      
      const getComponent = (type) => {
        const comp = addressComponents.find(c => c.types.includes(type));
        return comp ? comp.long_name : '';
      };

      return {
        storeAddress: result.formatted_address || '',
        storeCity: getComponent('locality') || getComponent('administrative_area_level_2') || '',
        storeState: getComponent('administrative_area_level_1') || '',
        storePincode: getComponent('postal_code') || '',
        storeLatitude: lat,
        storeLongitude: lng
      };
    } catch (error) {
      console.error('Error fetching address:', error);
      return null;
    }
  };

  const handleUpdateLocation = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const sellerIdStr = localStorage.getItem("sellerUserId") || sessionStorage.getItem("userId");
      const activeSellerId = sellerIdStr ? sellerIdStr.replace(/['"]+/g, '') : null;
      
      if (!activeSellerId) return;

      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
         formDataToSend.append(key, formData[key]);
      });
      
      const updateResponse = await putData(`seller/update/${activeSellerId}`, formDataToSend, token);
      
      if (updateResponse) {
         setSellerInfo({ ...sellerInfo, ...formData });
         setOpenLocationModal(false);
      }
    } catch (err) {
      console.error("Update failed", err);
    }
  };


  // the last 12 months, with a zero for months that had nothing
  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      const find = (list, key) => list.find((x) => x._id.year === d.getFullYear() && x._id.month === d.getMonth() + 1)?.[key] || 0;
      return { label: `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, invoices: find(graph.invoiceStats, "totalInvoices"), returns: find(graph.returnStats, "totalReturns") };
    });
  }, [graph]);

  const chartOptions = {
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: FONT },
    colors: [theme.palette.primary.main, theme.palette.error.main],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.04, stops: [20, 100, 100, 100] } },
    xaxis: { categories: months.map((m) => m.label), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#64748b" } } },
    yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: "#64748b" }, formatter: (v) => Math.round(v) } },
    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
    legend: { position: "top", horizontalAlign: "right" },
    tooltip: { theme: "light" },
  };
  const chartSeries = [{ name: "Invoices", data: months.map((m) => m.invoices) }, { name: "Returns", data: months.map((m) => m.returns) }];

  const topMax = Math.max(1, ...topProducts.map((x) => x.totalQty));
  const waiting = stats.requests.pending;

  return (
    <Box sx={{ width: "100%", p: { xs: 1.5, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: "#0f172a", lineHeight: 1.2 }}>Hi, {loggedInName || "Seller"}!</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#64748b" }}>
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} · your store at a glance
          </Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load} disabled={loading} sx={{ background: "#fff", border: "1px solid #e2e8f0" }}><Refresh /></IconButton></Tooltip>
      </Box>

      {failed > 0 && <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>{failed === 5 ? "The dashboard could not be loaded." : `${failed} section${failed > 1 ? "s" : ""} could not be loaded.`}</Alert>}
      {!loading && waiting > 0 && (
        <Alert severity="info" icon={<PendingActions />} sx={{ mb: 2, fontWeight: 600 }} action={<Button color="inherit" size="small" onClick={() => navigate("/orders")}>View requests</Button>}>
          {waiting} new order request{waiting > 1 ? "s are" : " is"} waiting for you to accept.
        </Alert>
      )}

      {/* Numbers that matter */}
      <Box data-tour="vendor-dashboard-summary" sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", mb: 2 }}>
        <Kpi label="New requests" value={stats.requests.pending} sub="waiting to be accepted" icon={<ShoppingCart fontSize="inherit" />} from="#f59e0b" to="#d97706" loading={loading} onClick={() => navigate("/orders")} />
        <Kpi label="In progress" value={stats.requests.inProgress} sub="accepted, packed or shipped" icon={<LocalShipping fontSize="inherit" />} from="#6366f1" to="#4338ca" loading={loading} onClick={() => navigate("/orders")} />
        <Kpi label="Delivered" value={stats.requests.delivered} sub={`of ${stats.requests.total} requests`} icon={<CheckCircle fontSize="inherit" />} from="#10b981" to="#047857" loading={loading} />
        <Kpi label="Products listed" value={stats.products.listed} sub="in your catalogue" icon={<Inventory fontSize="inherit" />} from="#0ea5e9" to="#1d4ed8" loading={loading} onClick={() => navigate("/products")} />
        <Kpi label="Pending payout" value={inr(stats.payout.pending)} sub="to be paid to you" icon={<AttachMoney fontSize="inherit" />} from="#ec4899" to="#be185d" loading={loading} onClick={() => navigate("/payouts")} />
      </Box>

      {/* How you are doing */}
      <Card tour="vendor-dashboard-rates" title="How you are doing" subtitle="Based on all your order requests" icon={<TrendingUp sx={{ fontSize: 20, color: "#6366f1" }} />} sx={{ mb: 2 }}>
        <Box sx={{ display: "grid", gap: { xs: 2, md: 4 }, gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" } }}>
          <Rate label="Fulfilment" value={stats.fulfillmentRate} hint="requests delivered" />
          <Rate label="Delivery success" value={stats.deliverySuccessRate} hint="delivered vs rejected" />
          <Rate label="Return rate" value={stats.returnRate} good="low" hint="lower is better" />
          <Rate label="Avg. dispatch time" value={stats.averageDispatchTimeHours} suffix="h" hint="request to shipped" />
        </Box>
      </Card>

      {/* Location summary */}
      {sellerInfo && (
        <Card tour="vendor-dashboard-business" title={sellerInfo.business_name || "Business details"} icon={<Business sx={{ fontSize: 20, color: theme.palette.primary.main }} />} accent="#0ea5e9" sx={{ mb: 2 }}
          right={<Button variant="outlined" size="small" startIcon={<EditLocationAlt />} onClick={() => setOpenLocationModal(true)} sx={{ fontWeight: 700, borderRadius: "8px", fontFamily: FONT, textTransform: "none" }}>Edit location</Button>}>
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            {[
              { title: "Business address", icon: <Business sx={{ fontSize: 15 }} />, a: sellerInfo.businessAddress, c: sellerInfo.businessCity, s: sellerInfo.businessState, p: sellerInfo.businessPincode },
              { title: "Store location", icon: <Storefront sx={{ fontSize: 15 }} />, a: sellerInfo.storeAddress, c: sellerInfo.storeCity, s: sellerInfo.storeState, p: sellerInfo.storePincode },
            ].map((x) => (
              <Box key={x.title}>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>{x.icon}{x.title}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: "#334155", mt: 0.25 }}>
                  {[x.a, x.c, x.s, x.p].filter(Boolean).join(", ") || "Not added yet"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Card>
      )}

      {/* Charts */}
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.7fr) minmax(0, 1fr)" }, mb: 2, alignItems: "stretch" }}>
        <Card tour="vendor-dashboard-graph" title="Last 12 months" subtitle="Invoices received vs returns" accent="#6366f1">
          {loading ? <Skeleton variant="rounded" height={300} /> : <ReactApexChart options={chartOptions} series={chartSeries} type="area" height={300} />}
        </Card>
        <Card tour="vendor-dashboard-top" title="Top supplied products" subtitle="By quantity" icon={<Star sx={{ fontSize: 20, color: "#f59e0b" }} />} accent="#f59e0b">
          {loading ? <Skeleton variant="rounded" height={260} /> : topProducts.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#94a3b8", py: 4, textAlign: "center" }}>Nothing supplied yet</Typography>
          ) : topProducts.slice(0, 5).map((item, i) => (
            <Box key={item.productId || i} sx={{ py: 1, borderBottom: i < Math.min(topProducts.length, 5) - 1 ? "1px solid #f1f5f9" : "none" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, mb: 0.5 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>{i + 1}. {item.productName}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 800, color: "#0f172a", flexShrink: 0 }}>{Number(item.totalQty).toLocaleString("en-IN")}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={(item.totalQty / topMax) * 100} sx={{ height: 6, borderRadius: 3, background: "#fef3c7", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#f59e0b,#fbbf24)", borderRadius: 3 } }} />
            </Box>
          ))}
        </Card>
      </Box>

      {/* Recent activity */}
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))" } }}>
        <Card tour="vendor-dashboard-requests" title="Recent order requests" icon={<ShoppingCart sx={{ fontSize: 20, color: "#6366f1" }} />} accent="#6366f1"
          right={<Button size="small" onClick={() => navigate("/orders")} sx={{ textTransform: "none", fontWeight: 700 }}>View all</Button>}>
          {loading ? <Skeleton variant="rounded" height={180} /> : recentRequests.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#94a3b8", py: 3, textAlign: "center" }}>No requests yet</Typography>
          ) : recentRequests.map((r, i) => (
            <Box key={r._id} onClick={() => navigate(`/orders/${r._id}`)} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.1, cursor: "pointer", borderBottom: i < recentRequests.length - 1 ? "1px dashed #e2e8f0" : "none", "&:hover": { background: "#f8fafc" } }}>
              <Typography sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12.5, fontWeight: 700, color: "#6366f1", minWidth: 92 }}>#{r.receiptId}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: "#1e293b", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>{r.product_id?.title || "N/A"}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#64748b" }}>{r.quantity || 0} units · {new Date(r.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</Typography>
              </Box>
              <StatusChip status={r.status} tones={REQUEST_TONE} />
            </Box>
          ))}
        </Card>

        <Card tour="vendor-dashboard-returns" title="Recent returns" icon={<AssignmentReturn sx={{ fontSize: 20, color: "#f43f5e" }} />} accent="#f43f5e">
          {loading ? <Skeleton variant="rounded" height={180} /> : recentReturns.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#94a3b8", py: 3, textAlign: "center" }}>No returns</Typography>
          ) : recentReturns.map((r, i) => (
            <Box key={r._id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.1, borderBottom: i < recentReturns.length - 1 ? "1px dashed #e2e8f0" : "none" }}>
              <Typography sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12.5, fontWeight: 700, color: "#f43f5e", minWidth: 92 }}>#{r.invoiceReturnId}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 800, color: "#1e293b" }}>{inr(r.totalReturnAmount)}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#64748b" }}>{(r.products || []).map((x) => x.productId?.title).filter(Boolean).slice(0, 2).join(", ") || new Date(r.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</Typography>
              </Box>
              <StatusChip status={r.status} tones={RETURN_TONE} />
            </Box>
          ))}
        </Card>
      </Box>

      {/* Edit Location Modal */}
      <Dialog 
        open={openLocationModal} 
        onClose={() => setOpenLocationModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: '24px',
            boxShadow: '0 24px 48px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          } 
        }}
      >
        <Box sx={{ 
          px: 4, 
          py: 3, 
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(244, 63, 94, 0.05) 100%)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h5" sx={{ fontFamily: "Mulish, sans-serif", fontWeight: 800, color: "#1e293b", display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EditLocationAlt sx={{ color: theme.palette.primary.main }} /> Edit Details
          </Typography>
          <IconButton onClick={() => setOpenLocationModal(false)} sx={{ color: '#64748b' }}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: { xs: 2, md: 4 }, background: '#f8fafc' }}>
          <Grid container spacing={4}>
            {/* Business Details Section */}
            <Grid size={12}>
               <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", fontFamily: "Mulish, sans-serif", display: 'flex', alignItems: 'center', gap: 1}}>
                    <Business sx={{ color: '#64748b', fontSize: 20 }}/> Business Location
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontFamily: "Mulish, sans-serif", mt: 0.5 }}>
                    Update your primary business address details below
                  </Typography>
               </Box>
               <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', background: '#ffffff' }}>
                  <Grid container spacing={2.5}>
                    <Grid size={12}>
                      <TextField fullWidth label="Business Address" variant="outlined" value={formData.businessAddress} onChange={(e) => setFormData({...formData, businessAddress: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField fullWidth label="City" variant="outlined" value={formData.businessCity} onChange={(e) => setFormData({...formData, businessCity: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField fullWidth label="State" variant="outlined" value={formData.businessState} onChange={(e) => setFormData({...formData, businessState: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField fullWidth label="Pincode" variant="outlined" value={formData.businessPincode} onChange={(e) => setFormData({...formData, businessPincode: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                  </Grid>
               </Paper>
            </Grid>

            {/* Store Location Section */}
            <Grid size={12}>
               <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", fontFamily: "Mulish, sans-serif", display: 'flex', alignItems: 'center', gap: 1}}>
                    <Storefront sx={{ color: '#64748b', fontSize: 20 }}/> Store Location
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontFamily: "Mulish, sans-serif", mt: 0.5 }}>
                    Where is your physical store or warehouse located?
                  </Typography>
               </Box>
               <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', background: '#ffffff' }}>
                  <Box sx={{ mb: 3 }}>
                    <MapPicker
                      lat={formData.storeLatitude}
                      lng={formData.storeLongitude}
                      onChange={async ({ lat, lng }) => {
                        const addr = await fetchAddressFromLatLng(lat, lng);
                        if (addr) {
                          setFormData(prev => ({ ...prev, ...addr }));
                        } else {
                          setFormData(prev => ({ ...prev, storeLatitude: lat, storeLongitude: lng }));
                        }
                      }}
                    />
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid size={12}>
                      <TextField fullWidth label="Store Address" variant="outlined" value={formData.storeAddress} onChange={(e) => setFormData({...formData, storeAddress: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField fullWidth label="City" variant="outlined" value={formData.storeCity} onChange={(e) => setFormData({...formData, storeCity: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField fullWidth label="State" variant="outlined" value={formData.storeState} onChange={(e) => setFormData({...formData, storeState: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField fullWidth label="Pincode" variant="outlined" value={formData.storePincode} onChange={(e) => setFormData({...formData, storePincode: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    </Grid>
                  </Grid>
               </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ p: 3, borderTop: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            onClick={() => setOpenLocationModal(false)} 
            variant="outlined" 
            sx={{ 
              fontWeight: 700, 
              borderRadius: '10px', 
              px: 3, 
              py: 1,
              color: '#64748b',
              borderColor: '#cbd5e1',
              fontFamily: "Mulish, sans-serif",
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#f8fafc'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateLocation} 
            variant="contained" 
            sx={{ 
              fontWeight: 700, 
              borderRadius: '10px', 
              px: 4, 
              py: 1,
              bgcolor: theme.palette.primary.main,
              fontFamily: "Mulish, sans-serif",
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                 bgcolor: '#4f46e5',
                 boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
              }
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
